import { motion } from 'framer-motion';
import { Instagram, Facebook, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/tryslushd/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/tryslushd', label: 'Facebook' },
  ];

  const navLinks = [
    { label: 'About', href: '#about' },
    { label: 'Flavors', href: '#flavors' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <footer 
      data-testid="footer"
      className="relative bg-[#0a0a0a] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl font-black text-gradient uppercase tracking-tighter mb-4">
                SLUSH'D
              </h3>
              <p className="text-white/60 leading-relaxed max-w-sm mb-6">
                Premium mobile slush drinks for festivals, events, and parties. 
                Bringing the chill to your celebration since 2017.
              </p>
              
              {/* Social Links */}
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-[#CCFF00] hover:bg-[#CCFF00]/10 transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Navigation Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-6">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      data-testid={`footer-nav-${link.label.toLowerCase()}`}
                      className="text-white/60 hover:text-[#CCFF00] transition-colors duration-300"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-6">
                Contact
              </h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-white/60">
                  <Mail className="w-5 h-5 text-[#CCFF00]" />
                  <span>hello@slushd.co</span>
                </li>
                <li className="flex items-center gap-3 text-white/60">
                  <Phone className="w-5 h-5 text-[#CCFF00]" />
                  <span>+1 (785) 580-6025</span>
                </li>
                <li className="flex items-start gap-3 text-white/60">
                  <MapPin className="w-5 h-5 text-[#CCFF00] flex-shrink-0" />
                  <span>Serving events nationwide</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-white/40 text-sm">
            © {currentYear} SLUSHd. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Made with <span className="text-[#FF0099]">♥</span> for festival lovers
          </p>
        </motion.div>
      </div>

      {/* Bottom Glow */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#CCFF00]/30 to-transparent" />
    </footer>
  );
};

export default Footer;
